import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

export const LocationSearch = ({ value, onSelect, onTextChange, placeholder, testId, inputClassName = "" }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  const boxRef = useRef();
  // True when the current text maps to picked/known coords. Typing free text
  // flips it false; blur then auto-resolves the top suggestion so submissions
  // rarely leave without lat/lng (backend geocode is the final safety net).
  const resolvedRef = useRef(true);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    resolvedRef.current = false;
    onTextChange?.(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.geoSearch(v.trim());
        setResults(res);
        setOpen(res.length > 0);
      } catch { setResults([]); }
      setLoading(false);
    }, 350);
  };

  const pick = (r) => {
    resolvedRef.current = true;
    setQuery(r.label);
    setOpen(false);
    onSelect(r);
  };

  const onBlur = (e) => {
    // If focus moved to a suggestion in the dropdown, let its click handle it.
    if (boxRef.current && boxRef.current.contains(e.relatedTarget)) return;
    const text = (e.target.value || "").trim();
    if (resolvedRef.current || text.length < 2) return;
    const fromList = results[0];
    setTimeout(async () => {
      if (resolvedRef.current) return; // user picked meanwhile
      let top = fromList;
      if (!top) {
        try { top = (await api.geoSearch(text))[0]; } catch { top = null; }
      }
      if (top && !resolvedRef.current) pick(top);
    }, 250);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        data-testid={testId || "location-search-input"}
        value={query}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder || "Town, city or UK postcode"}
        className={`h-11 pr-9 ${inputClassName}`}
      />
      {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" />}
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden">
          {results.map((r, i) => (
            <button
              key={`${r.label}-${i}`}
              data-testid={`geo-result-${i}`}
              onClick={() => pick(r)}
              className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 hover:bg-forest/5 flex items-center gap-2"
            >
              <MapPin className="w-3.5 h-3.5 text-terracotta shrink-0" /> {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
